import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDatabase } from '../contexts/DatabaseContext';
import ErrorBanner from './ErrorBanner';

function FunFactForm() {
    useEffect(() => {
        document.title = 'Sydney Ferris - Fun Facts';
    }, []);
    const { user, login, logout, loading: authLoading } = useAuth();
    const { addDocument } = useDatabase();
    const [formData, setFormData] = useState({
        fact: '',
        category: 'General'
    });

    const navigate = useNavigate();
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!user) {
            alert('Please sign in with Google to submit a fun fact');
            return;
        }

        try {
            const documentData = {
                fact: formData.fact,
                category: formData.category,
                submittedBy: user.name,
                submittedByEmail: user.email,
                submittedByUid: user.uid,
                status: 'pending'
            };
            console.log('📝 Submitting fun fact with data:', JSON.stringify(documentData, null, 2));
            console.log('📝 User info:', JSON.stringify(user, null, 2));
            
            await addDocument('funFacts', documentData);

            alert('Fun fact submitted successfully! It will be visible after approval.');
            setFormData({ fact: '', category: 'General' });
            navigate('/fun-facts-display');
        } catch (error) {
            console.error('Error submitting fun fact:', error);
            alert('Failed to submit fun fact. Please try again later.');
        }
      };

      const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevState => ({
          ...prevState,
          [name]: value
        }));
      };

      const handleGoogleLogin = async () => {
        try {
          await login();
        } catch (error) {
          console.error('Google Login Failed:', error);
        }
      };

  return (
    <div className="isolate theme-bg-secondary min-h-[calc(100vh-5rem)] px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <div className="mx-auto max-w-7xl text-center px-4">
        <ErrorBanner 
          title="Under Construction" 
          message="This form is currently under construction. Features may not work as expected." 
          type="warning" 
        />
        <h1 className="text-2xl sm:text-3xl lg:text-hero font-bold theme-primary mb-6 font-GoogleSans">Share a Fun Fact</h1>
        <p className="text-sm sm:text-base lg:text-subhero theme-secondary mb-8 sm:mb-12 max-w-3xl mx-auto">
          Did you have an interesting fact to share? Share it with me!
        </p>
      </div>

      {!user ? (
        <div className="mx-auto mt-8 max-w-xl text-center px-4">
          <div className="theme-bg-secondary rounded-lg p-6 sm:p-8">
            <h3 className="text-lg font-semibold theme-primary mb-4">Sign in Required</h3>
            <p className="theme-secondary mb-6 text-sm sm:text-base">Please sign in with your Google account to submit a fun fact.</p>
            <div className="flex justify-center">
              <button
                onClick={handleGoogleLogin}
                disabled={authLoading}
                className="px-6 py-3 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors border border-gray-300 shadow-sm disabled:opacity-50"
              >
                {authLoading ? 'Signing in...' : 'Sign in with Google'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="mx-auto mt-8 max-w-xl mb-4 px-4 flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-semibold">{user.name.charAt(0)}</span>
              </div>
              <div>
                <p className="text-sm font-medium theme-primary">{user.name}</p>
                <p className="text-xs text-gray-500 hidden sm:block">{user.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="text-sm theme-secondary hover:theme-primary"
            >
              Sign Out
            </button>
          </div>
          <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-xl px-4">
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="category" className="block text-sm font-semibold leading-6 theme-primary">
                  Category
                </label>
                <div className="mt-2.5">
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 theme-primary shadow-sm ring-1 ring-inset theme-border focus:ring-2 focus:ring-inset focus:ring-theme-accent sm:text-sm sm:leading-6 theme-bg"
                  >
                    <option value="General">General</option>
                    <option value="Science">Science</option>
                    <option value="History">History</option>
                    <option value="Technology">Technology</option>
                    <option value="Nature">Nature</option>
                    <option value="Sports">Sports</option>
                    <option value="Food">Food</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold leading-6 theme-primary">
                  Your Fun Fact
                </label>
                <div className="mt-2.5">
                  <textarea
                    name="fact"
                    onChange={handleChange}
                    value={formData.fact}
                    rows={6}
                    placeholder="Did you know that..."
                    className="block w-full rounded-md border-0 px-3.5 py-2 theme-primary theme-bg shadow-sm ring-1 ring-inset theme-border placeholder:theme-tertiary focus:ring-2 focus:ring-inset focus:ring-theme-accent sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="mt-10">
              <button
                type="submit"
                className="block w-full rounded-md tracking-widest theme-bg-accent px-3.5 py-2.5 text-center text-sm font-semibold theme-accent-contrast shadow-sm hover:opacity-80"
              >
                Submit Fun Fact
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

export default FunFactForm;